import type { ColDef } from '@ag-grid-community/core';
import type {
  AggregateReportConfig,
  ReportData,
} from '../report-viewer/report-viewer.model';

/**
 * Column Definitions for Chrome Version Age by OU Report
 */
export const CHROME_VERSION_AGE_COLUMN_DEFS: ColDef[] = [
  {
    headerName: 'Major Chrome Version',
    field: 'chromeVersion',
    flex: 1,
  },
  {
    headerName: 'OU Path',
    field: 'ouPath',
    flex: 2,
  },
  {
    headerName: 'Major Chrome Version Devices',
    field: 'activeDevices',
    flex: 1,
  },
  {
    headerName: 'Version Release Date',
    field: 'releaseDate',
    flex: 1,
  },
];

/**
 * Configuration for Chrome Version Age by OU aggregate report
 */
export function createChromeVersionAgeReportConfig(
  data: unknown[]
): ReportData {
  // Sort data by Chrome version (descending - highest first), then by OU path (ascending)
  const sortedData = [...data].sort((a, b) => {
    const itemA = a as { chromeVersion: string | number; ouPath: string };
    const itemB = b as { chromeVersion: string | number; ouPath: string };

    // First, sort by Chrome version (descending - highest version first)
    const versionA = Number(itemA.chromeVersion);
    const versionB = Number(itemB.chromeVersion);

    // Handle NaN cases - put them at the end. The synthetic 'Unknown' bucket
    // (EDUS-2438) is non-numeric and naturally falls into this branch so it
    // sorts to the bottom of the Chrome-version hierarchy.
    if (Number.isNaN(versionA) && Number.isNaN(versionB)) {
      // Both are NaN, sort by OU path
      return (itemA.ouPath || '').localeCompare(itemB.ouPath || '');
    }
    if (Number.isNaN(versionA)) return 1; // A is NaN, goes after B
    if (Number.isNaN(versionB)) return -1; // B is NaN, goes after A

    // Both are valid numbers, sort descending (highest first)
    if (versionB !== versionA) {
      return versionB - versionA;
    }

    // Same version, sort by OU path (ascending)
    return (itemA.ouPath || '').localeCompare(itemB.ouPath || '');
  });

  const aggregateConfig: AggregateReportConfig = {
    columns: CHROME_VERSION_AGE_COLUMN_DEFS,
    groupFields: ['chromeVersion', 'ouPath'],
    aggregationField: 'activeDevices',
    autoGroupColumnName: 'Major Chrome Version / OU Path',
    data: sortedData,
  };

  return {
    type: 'chrome-version-age',
    data: sortedData,
    aggregateConfig,
  };
}
